import { Input, Select, SelectItem, Spinner, Switch, Textarea } from '@nextui-org/react';
import { useMemo, useRef, useState, type RefObject } from 'react';
import { AdvisorStatus, useGetAdvisorsQuery } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import { handleMarkdownInsert, MarkdownToolbar, markdownPreviewStyles, processMarkdownText } from '../utils/markdownUtils';
import { Icon } from '@iconify/react';
import ReactMarkdown from 'react-markdown';

type JsonPrimitive = string | number | boolean | null;
interface JsonObject {
    [key: string]: JsonValue;
}
interface JsonArray extends Array<JsonValue> { }
export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

interface GenericConfigurationFieldRendererProps {
    fieldKey: string;
    fieldLabel: string;
    value: JsonValue | undefined;
    initialValue: JsonValue | undefined;
    isPreview: boolean;
    onValueChange: (value: JsonValue) => void;
    onPreviewChange: (isPreview: boolean) => void;
    onResetToDefault: () => void;
    onDelete: () => void;
    t: (value: string) => string;
    isDisabled?: boolean;
}

type AdvisorSelectFieldProps = {
    fieldLabel: string;
    value: string;
    isDisabled: boolean;
    deleteButton: JSX.Element;
    onValueChange: (value: JsonValue) => void;
};

const AdvisorSelectField = ({
    fieldLabel,
    value,
    isDisabled,
    deleteButton,
    onValueChange,
}: AdvisorSelectFieldProps) => {
    const { data: advisorListData, isLoading, isFetching } = useGetAdvisorsQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    const advisors = useMemo(
        () =>
            (advisorListData?.data ?? []).filter(
                (advisor) =>
                    advisor.status !== AdvisorStatus.DELETED &&
                    advisor.status !== AdvisorStatus.DELETION_IN_PROGRESS
            ),
        [advisorListData?.data]
    );

    const selectedKeys = value ? new Set([value]) : new Set<string>();

    return (
        <div className="flex items-center gap-2">
            <Select
                label={fieldLabel}
                placeholder='Select an Advisor'
                variant="bordered"
                size="sm"
                className="flex-1"
                isDisabled={isDisabled || isLoading || isFetching}
                selectedKeys={selectedKeys}
                onChange={(e) => {
                    onValueChange(e.target.value);
                }}
                aria-label={fieldLabel}
                startContent={
                    isLoading || isFetching ? <Spinner size="sm" /> : undefined
                }
            >
                {advisors.map((advisor) => (
                    <SelectItem key={advisor.advisor_id} value={advisor.advisor_id} textValue={advisor.name}>
                        {advisor.name}
                    </SelectItem>
                ))}
            </Select>
            {deleteButton}
        </div>
    );
};

type RenderStringFieldArgs = {
    value: string;
    fieldKey: string;
    fieldLabel: string;
    isPreview: boolean;
    isDisabled: boolean;
    deleteButton: JSX.Element;
    onValueChange: (value: JsonValue) => void;
    onPreviewChange: (isPreview: boolean) => void;
    onResetToDefault: () => void;
    onCopy: () => void;
    isCopied: boolean;
    t: (value: string) => string;
    textareaRef: RefObject<HTMLTextAreaElement>;
    handleMarkdownAction: (action: string) => void;
};

const renderStringField = ({
    value,
    fieldKey,
    fieldLabel,
    isPreview,
    isDisabled,
    deleteButton,
    onValueChange,
    onPreviewChange,
    onResetToDefault,
    onCopy,
    isCopied,
    t,
    textareaRef,
    handleMarkdownAction,
}: RenderStringFieldArgs) => {
    const isPromptField = fieldKey.toLowerCase().includes('prompt') || fieldLabel.toLowerCase().includes('prompt');
    if (!isPromptField) {
        const estimatedRows = Math.min(20, Math.max(1, Math.ceil(value.length / 80)));
        return (
            <div className="flex items-center gap-2">
                <Textarea
                    value={value}
                    onValueChange={(next) => onValueChange(next)}
                    label={fieldLabel}
                    variant="bordered"
                    size="sm"
                    isDisabled={isDisabled}
                    className="flex-1"
                    minRows={estimatedRows}
                    maxRows={estimatedRows + 2}
                />
                {deleteButton}
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-1 rounded-lg border border-divider bg-content1 p-3">
            <div className="flex w-full items-center justify-between gap-2">
                <span className="text-sm font-medium text-default-700">{fieldLabel}</span>
                <div className="flex items-center gap-2">
                    <CtyButton
                        size="sm"
                        color={isPreview ? 'default' : 'primary'}
                        variant={isPreview ? 'bordered' : 'solid'}
                        onPress={() => onPreviewChange(false)}
                    >
                        {t('Edit')}
                    </CtyButton>
                    <CtyButton
                        size="sm"
                        color={isPreview ? 'primary' : 'default'}
                        variant={isPreview ? 'solid' : 'bordered'}
                        onPress={() => onPreviewChange(true)}
                    >
                        {t('Preview')}
                    </CtyButton>
                </div>
            </div>
            <div className="flex w-full items-center justify-between gap-2">
                {!isPreview && !isDisabled ? <MarkdownToolbar onInsert={handleMarkdownAction} /> : <div />}
                <div className="flex items-center gap-2">
                    <CtyButton
                        size="sm"
                        variant="bordered"
                        isDisabled={isDisabled}
                        onPress={onCopy}
                        startContent={<Icon icon="hugeicons:copy-01" className="text-base" />}
                    >
                        {isCopied ? t('Copied') : t('Copy')}
                    </CtyButton>
                    <CtyButton
                        size="sm"
                        variant="bordered"
                        isDisabled={isDisabled}
                        onPress={onResetToDefault}
                    >
                        {t('Reset to Default')}
                    </CtyButton>
                    {deleteButton}
                </div>
            </div>

            {isPreview ? (
                <>
                    <style>{markdownPreviewStyles}</style>
                    <div className="markdown-preview markdown-body rounded-lg border border-divider bg-content1 p-4 text-sm max-h-[520px] overflow-y-auto">
                        <ReactMarkdown>{processMarkdownText(value)}</ReactMarkdown>
                    </div>
                </>
            ) : (
                <div className="flex items-end gap-2">
                    <Textarea
                        ref={textareaRef}
                        minRows={14}
                        value={value}
                        isReadOnly={isDisabled}
                        onValueChange={(next) => onValueChange(next)}
                        variant="bordered"
                        className="flex-1"
                        classNames={{ inputWrapper: 'bg-content1', input: 'font-mono text-sm leading-6' }}
                    />
                </div>
            )}
        </div>
    );
};

export default function GenericConfigurationFieldRenderer({
    fieldKey,
    fieldLabel,
    value,
    initialValue,
    isPreview,
    onValueChange,
    onPreviewChange,
    onResetToDefault,
    onDelete,
    t,
    isDisabled = false,
}: Readonly<GenericConfigurationFieldRendererProps>) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isCopied, setIsCopied] = useState(false);
    const handleCopySection = async () => {
        const text = typeof value === 'string' ? value : JSON.stringify(value ?? '', null, 2);
        try {
            await navigator.clipboard.writeText(text);
            setIsCopied(true);
            CtyShowToast(checkType.success, t('Copied to clipboard'));
        } catch {
            CtyShowToast(checkType.error, t('Failed to copy'));
        }
    };

    const deleteButton = (
        <CtyButton
            isIconOnly
            size="sm"
            color="danger"
            variant="flat"
            onPress={onDelete}
            isDisabled={isDisabled}
            aria-label={t('Delete field')}
            className="shrink-0 min-w-8 h-8 rounded-md border border-danger bg-content1"
        >
            <Icon icon="mdi:delete-outline" className="text-lg text-danger" />
        </CtyButton>
    );

    const handleMarkdownAction = (action: string) => {
        handleMarkdownInsert(
            action,
            textareaRef.current,
            () => String(value ?? ''),
            (nextValue: string) => onValueChange(nextValue)
        );
    };

    if (typeof value === 'boolean') {
        return (
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-3 flex-nowrap rounded-lg border border-divider bg-content1 px-4 py-2 flex-1">
                    <span className="text-sm font-medium text-default-700 min-w-0 flex-1">{fieldLabel}</span>
                    <div className="ml-auto flex items-center gap-2 shrink-0">
                        <Switch className="shrink-0" isSelected={Boolean(value)} isDisabled={isDisabled} onValueChange={(next) => onValueChange(Boolean(next))} />
                    </div>
                </div>
                {deleteButton}
            </div>
        );
    }

    if (typeof value === 'number') {
        return (
            <div className="flex items-center gap-2">
                <Input
                    type="number"
                    value={String(value)}
                    onValueChange={(next) => onValueChange(Number(next || 0))}
                    label={fieldLabel}
                    variant="bordered"
                    size="sm"
                    isDisabled={isDisabled}
                    className="flex-1"
                />
                {deleteButton}
            </div>
        );
    }

    if (typeof value === 'string') {
        if (fieldKey.toLowerCase() === 'advisor') {
            return (
                <AdvisorSelectField
                    fieldLabel={fieldLabel}
                    value={value}
                    isDisabled={isDisabled}
                    deleteButton={deleteButton}
                    onValueChange={onValueChange}
                />
            );
        }

        return renderStringField({
            value,
            fieldKey,
            fieldLabel,
            isPreview,
            isDisabled,
            deleteButton,
            onValueChange,
            onPreviewChange,
            onResetToDefault,
            onCopy: handleCopySection,
            isCopied,
            t,
            textareaRef,
            handleMarkdownAction,
        });
    }

    if (value === null) {
        return (
            <div className="flex items-center gap-2">
                <Input
                    value=""
                    onValueChange={(next) => onValueChange(next)}
                    label={fieldLabel}
                    variant="bordered"
                    size="sm"
                    isDisabled={isDisabled}
                    className="flex-1"
                />
                {deleteButton}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <Textarea
                minRows={16}
                isReadOnly
                value={JSON.stringify(value ?? {}, null, 2)}
                label={fieldLabel || t('JSON Value')}
                variant="bordered"
                classNames={{ inputWrapper: 'bg-content1', input: 'font-mono text-xs leading-5' }}
            />
            <div className="flex justify-end">
                {deleteButton}
            </div>
        </div>
    );
}
